"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import DeleteConfirmationModal from "@/components/delete-confirmation-modal";
import AppointmentLocationPrompt from "@/components/location/appointment-location-prompt";
import AnnotationHistoryModal from "@/components/scribe/annotation-history-modal";
import { Button } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { queryClient } from "@/config/react-query";
import { computePersonMask, getPersonSegmenter } from "@/lib/photo-background";
import { ensureStudioMask, isStudioPhoto, restudioEditedPhoto } from "@/lib/photo-studio/studio-photo";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { Annotation, CropRect } from "@/types/annotation";
import { NoteAttachment } from "@/types/notes";
import { getSignedUrlBase64 } from "@/api/auth/gcs";
import { photoStudioQueryKeys, photoStudioQueryOptions, useAttachmentStudio, useRevertAttachmentToOriginal } from "@/api/provider/photo-studio/studio-query";
import { replaceAttachmentImage } from "@/api/provider/scribe/notes";
import { eventQueryOptions } from "@/query/check-in/calendar/event-query";
import { AttachmentAnnotationDraftScope, useAttachmentAnnotationDraftStore } from "@/stores/attachment-annotation-draft-store";

// Dynamically import ImageAnnotator with SSR disabled
const ImageAnnotator = dynamic(() => import("@/app/[tenant]/(console)/scribe/playground/[id]/image-annotator"), {
  ssr: false,
  loading: () => <div className="flex h-[70vh] items-center justify-center">
        <div className="text-muted-foreground">Loading annotation tools...</div>
      </div>
});
type AttachmentAnnotationExtraActions = ReactNode | ((state: {
  hasUnsavedChanges: boolean;
}) => ReactNode);
interface AttachmentAnnotationEditorProps {
  open: boolean;
  attachment: NoteAttachment | null;
  /** Patient being charted, forwarded to the lot picker so it can offer lots
   *  today's checkout already drew for them. */
  patientId?: string;
  /** Appointment the photo was taken for. Face chart doses and the lots offered
   *  come from the location the user is working at, so the editor offers to
   *  switch to the appointment's location when it differs. */
  appointmentId?: string | null;
  initialAnnotations?: Annotation[];
  /**
   * Persist annotations to the server. Receives the latest local state PLUS
   * the baseline (what the server last saw) so the caller can compute a diff
   * and send a single bulk-apply request.
   */
  onSave: (annotations: Annotation[], baseline: Annotation[]) => Promise<void>;
  onClose: () => boolean | void;
  onImageReplaced?: () => void;
  onRestore?: (historyId: string) => Promise<void>;
  isRestoring?: boolean;
  isSaving?: boolean;
  imageFile?: File;
  isFullScreen?: boolean;
  imageClassName?: string;
  actionsClassName?: string;
  closeLabel?: string;
  extraActions?: AttachmentAnnotationExtraActions;
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
  annotationDraftScope?: AttachmentAnnotationDraftScope | null;
  alwaysLabelMuscles?: boolean;
}
export function AttachmentAnnotationEditor({
  open,
  attachment,
  patientId,
  appointmentId,
  initialAnnotations = [],
  onSave,
  onClose,
  onImageReplaced,
  onRestore,
  isRestoring = false,
  isSaving = false,
  imageFile,
  isFullScreen = false,
  imageClassName,
  actionsClassName,
  closeLabel = "Cancel",
  extraActions,
  onUnsavedChangesChange,
  annotationDraftScope = null,
  alwaysLabelMuscles = false
}: AttachmentAnnotationEditorProps) {
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const {
    data: appointment
  } = useQuery({
    ...eventQueryOptions.detail(appointmentId ?? ""),
    enabled: open && !!appointmentId
  });
  const annotationDraftScopeKey = useMemo(() => annotationDraftScope ? useAttachmentAnnotationDraftStore.getState().getScopeKey(annotationDraftScope) : null, [annotationDraftScope]);
  const getAnnotationDraft = () => annotationDraftScopeKey ? useAttachmentAnnotationDraftStore.getState().drafts[annotationDraftScopeKey] : undefined;
  const setAnnotationDraft = useAttachmentAnnotationDraftStore(state => state.setDraft);
  const clearAnnotationDraft = useAttachmentAnnotationDraftStore(state_0 => state_0.clearScope);
  const hydratedAnnotationDraftScopeKey = useRef<string | null>(null);
  const [currentAnnotations, setCurrentAnnotations] = useState<Annotation[]>(() => getAnnotationDraft()?.annotations ?? initialAnnotations);
  // Baseline = what the server last saw. Reset on open/restore so the diff
  // sent on Save only covers this session's edits.
  const [savedAnnotations, setSavedAnnotations] = useState<Annotation[]>(() => getAnnotationDraft()?.baseline ?? initialAnnotations);
  const [isApplyingTransform, setIsApplyingTransform] = useState(false);
  const [showRestoreOriginal, setShowRestoreOriginal] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | undefined>();
  // Rotate, crop and restore point the attachment at a new storage object. The
  // attachment prop can be a snapshot that never sees that, so keep the latest
  // path here; otherwise a second rotate would start again from the old image.
  const [currentGcsPath, setCurrentGcsPath] = useState<string | undefined>();
  const activeGcsPath = currentGcsPath ?? attachment?.gcsPath;

  // Studio Mode: rotate/crop keep the original, so it can be restored, and the
  // cutout is recomputed for the edited image.
  const studioAttachmentId = open && !imageFile ? attachment?.attachmentId : undefined;
  const {
    data: studio
  } = useAttachmentStudio(studioAttachmentId);
  const revertToOriginal = useRevertAttachmentToOriginal();
  const [imageVersion, setImageVersion] = useState(0);
  const [restoreTimestamp, setRestoreTimestamp] = useState<number | null>(null);
  const resetState = () => {
    if (annotationDraftScope) {
      clearAnnotationDraft(annotationDraftScope);
    }
    hydratedAnnotationDraftScopeKey.current = null;
    setCurrentAnnotations(initialAnnotations);
    setSavedAnnotations(initialAnnotations);
    setCurrentImageUrl(undefined);
    setCurrentGcsPath(undefined);
    setImageVersion(0);
    hasAppliedTransform.current = false;
    setPendingTransform(null);
    setShowTransformWarning(false);
  };
  const handleClose = () => {
    const didClose = onClose();
    if (didClose === false) return;
    resetState();
  };
  const handleRestoreComplete = () => {
    // Set timestamp to track that a restore just happened
    // This will be used in the useEffect to add cache-busting to the image URL
    const timestamp = Date.now();
    // Force refresh of image and annotations by incrementing version
    // This will cause ImageAnnotator to remount with fresh data
    setImageVersion(prev => prev + 1);
    // The attachment prop will be updated by React Query after refetch
    // We'll add cache-busting in the useEffect when attachment updates
    // Store timestamp for use in useEffect
    setRestoreTimestamp(timestamp);
    // Close the attachment annotation editor after restore
    handleClose();
  };
  const handleViewHistory = () => {
    setHistoryModalOpen(true);
  };

  // Track if we've applied a transformation to prevent resetting
  const hasAppliedTransform = useRef(false);

  // Pending transform confirmation
  const [pendingTransform, setPendingTransform] = useState<{
    rotation: number;
    crop?: CropRect;
  } | null>(null);
  const [showTransformWarning, setShowTransformWarning] = useState(false);
  const hasUnsavedAnnotationChanges = useMemo(() => JSON.stringify(currentAnnotations) !== JSON.stringify(savedAnnotations), [currentAnnotations, savedAnnotations]);
  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedAnnotationChanges);
  }, [hasUnsavedAnnotationChanges, onUnsavedChangesChange]);
  const resolvedExtraActions = typeof extraActions === "function" ? extraActions({
    hasUnsavedChanges: hasUnsavedAnnotationChanges
  }) : extraActions;
  const handleSave = async () => {
    await onSave(currentAnnotations, savedAnnotations);
    // After a successful save, the server's view matches local — advance the
    // baseline so the next save only diffs against this state.
    setSavedAnnotations(currentAnnotations);
    if (annotationDraftScope) {
      clearAnnotationDraft(annotationDraftScope);
    }
    hydratedAnnotationDraftScopeKey.current = null;
  };

  // Request transformation - show warning first if there are annotations
  const handleRequestTransform = async (rotation: number, crop?: CropRect) => {
    if (currentAnnotations.length > 0) {
      setPendingTransform({
        rotation,
        crop
      });
      setShowTransformWarning(true);
    } else {
      // No annotations, apply directly
      await applyTransform(rotation, crop);
    }
  };

  // Apply transformation after confirmation
  const applyTransform = async (rotation_0: number, crop_0?: CropRect) => {
    if (!attachment?.attachmentId || !activeGcsPath) {
      toast({
        title: "Error",
        description: "Cannot apply transformation. Attachment not ready.",
        variant: "error"
      });
      return;
    }
    setIsApplyingTransform(true);
    try {
      // Fetch image as base64 to avoid CORS issues
      const base64Response = await getSignedUrlBase64(activeGcsPath);
      if (!base64Response?.signedUrl) {
        throw new Error("Failed to fetch image");
      }

      // Load the image
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = base64Response.signedUrl;
      });

      // Calculate dimensions after any rotation angle
      const radians = rotation_0 * Math.PI / 180;
      const cos = Math.abs(Math.cos(radians));
      const sin = Math.abs(Math.sin(radians));
      const rotatedWidth = Math.round(img.naturalWidth * cos + img.naturalHeight * sin);
      const rotatedHeight = Math.round(img.naturalWidth * sin + img.naturalHeight * cos);

      // Calculate final dimensions (with crop if applicable)
      const activeCrop = crop_0 || {
        x: 0,
        y: 0,
        width: 1,
        height: 1
      };
      const finalWidth = Math.round(activeCrop.width * rotatedWidth);
      const finalHeight = Math.round(activeCrop.height * rotatedHeight);
      const cropX = Math.round(activeCrop.x * rotatedWidth);
      const cropY = Math.round(activeCrop.y * rotatedHeight);

      // Create canvas
      const canvas = document.createElement("canvas");
      canvas.width = finalWidth;
      canvas.height = finalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Failed to create canvas context");

      // Apply transformations
      ctx.save();
      ctx.translate(-cropX, -cropY);
      if (rotation_0 !== 0) {
        // Translate to center of rotated bounds, rotate, then translate back
        ctx.translate(rotatedWidth / 2, rotatedHeight / 2);
        ctx.rotate(radians);
        ctx.translate(-img.naturalWidth / 2, -img.naturalHeight / 2);
      }
      ctx.drawImage(img, 0, 0);
      ctx.restore();

      // Convert canvas to blob
      const blob = await new Promise<Blob>((resolve_0, reject_0) => {
        canvas.toBlob(b => b ? resolve_0(b) : reject_0(new Error("Failed to create blob")), "image/jpeg", 0.92);
      });

      // Create file from blob
      const file = new File([blob], "transformed-image.jpg", {
        type: "image/jpeg"
      });

      // Upload to replace original
      const response = await replaceAttachmentImage(attachment.attachmentId, file);
      if (response) {
        if (response.gcsPath) {
          setCurrentGcsPath(response.gcsPath);
        }
        if (studioAttachmentId && studio && isStudioPhoto(studio) && response.gcsPath) {
          // The server dropped the old cutout: it no longer lines up.
          const segmenter = await getPersonSegmenter();
          const mask = segmenter ? await computePersonMask(segmenter, canvas) : null;
          void restudioEditedPhoto(studioAttachmentId, response.gcsPath, mask).finally(() => queryClient.invalidateQueries({
            queryKey: photoStudioQueryKeys.attachment(studioAttachmentId)
          }));
        } else if (studioAttachmentId) {
          void queryClient.invalidateQueries({
            queryKey: photoStudioQueryKeys.attachment(studioAttachmentId)
          });
        }
        // Mark that we've applied a transform
        hasAppliedTransform.current = true;
        // Update the current image URL to the new one
        setCurrentImageUrl(response.signedUrl);
        // Force re-render of the annotator
        setImageVersion(v => v + 1);
        // Clear annotations since the image changed
        setCurrentAnnotations([]);
        if (annotationDraftScope) {
          clearAnnotationDraft(annotationDraftScope);
        }
        hydratedAnnotationDraftScopeKey.current = null;
        // Notify parent to refresh
        onImageReplaced?.();
        toast({
          title: "Image Updated",
          description: "Transformation applied successfully.",
          variant: "success"
        });
      }
    } catch (error) {
      console.error("Failed to apply transformation:", error);
      toast({
        title: "Error",
        description: "Failed to apply transformation. Please try again.",
        variant: "error"
      });
    } finally {
      setIsApplyingTransform(false);
    }
  };
  const handleRestoreOriginal = async () => {
    if (!studioAttachmentId) return;
    const wasStudioPhoto = !!studio && isStudioPhoto(studio);
    try {
      const restored = await revertToOriginal.mutateAsync(studioAttachmentId);
      hasAppliedTransform.current = true;
      if (restored.gcsPath) {
        setCurrentGcsPath(restored.gcsPath);
      }
      if (wasStudioPhoto) {
        // The server drops the cutout of the edited photo. Make one for the
        // original so it goes back on its backdrop, as it was before the edit.
        void queryClient.fetchQuery({
          ...photoStudioQueryOptions.attachment(studioAttachmentId),
          staleTime: 0
        }).then(state_1 => ensureStudioMask(state_1)).catch(error_1 => console.error("Studio update failed after restoring:", error_1));
      }
      setCurrentImageUrl(restored.signedUrl);
      setImageVersion(v_0 => v_0 + 1);
      // The server clears annotations, as it does for rotate and crop.
      setCurrentAnnotations([]);
      setSavedAnnotations([]);
      if (annotationDraftScope) {
        clearAnnotationDraft(annotationDraftScope);
      }
      hydratedAnnotationDraftScopeKey.current = null;
      onImageReplaced?.();
      toast({
        title: "Original restored",
        description: "The photo is back to how it was taken.",
        variant: "success"
      });
    } catch (error_0) {
      console.error("Failed to restore the original photo:", error_0);
      toast({
        title: "Error",
        description: "Couldn't restore the original photo. Please try again.",
        variant: "error"
      });
    }
  };
  const handleConfirmTransform = () => {
    if (pendingTransform) {
      applyTransform(pendingTransform.rotation, pendingTransform.crop);
      setPendingTransform(null);
    }
    setShowTransformWarning(false);
  };

  // Update state when attachment changes - but not if we've applied a transform
  useEffect(() => {
    if (attachment && open && !hasAppliedTransform.current) {
      const draft = getAnnotationDraft();
      const serverAnnotations = attachment.annotations || initialAnnotations;
      setCurrentAnnotations(draft?.annotations ?? serverAnnotations);
      setSavedAnnotations(draft?.baseline ?? serverAnnotations);
      hydratedAnnotationDraftScopeKey.current = annotationDraftScopeKey;
      // If we just restored, add cache-busting parameter to force image refresh
      // Use signedUrl if available (from API), otherwise use url (client-side)
      const imageUrl = attachment.signedUrl || attachment.url;
      if (!imageUrl) {
        console.error("No image URL available for attachment");
        return;
      }
      let finalImageUrl = imageUrl;
      if (restoreTimestamp) {
        const separator = imageUrl.includes("?") ? "&" : "?";
        finalImageUrl = `${imageUrl}${separator}_restore=${restoreTimestamp}`;
        // Clear restore timestamp after using it
        setRestoreTimestamp(null);
      }
      setCurrentImageUrl(finalImageUrl);
      // Don't reset imageVersion to 0 here - let it increment on restore to force refresh
    }
  }, [attachment, open, initialAnnotations, restoreTimestamp, annotationDraftScopeKey]);
  useEffect(() => {
    if (!open || !attachment || !annotationDraftScope || hydratedAnnotationDraftScopeKey.current !== annotationDraftScopeKey) {
      return;
    }
    setAnnotationDraft(annotationDraftScope, currentAnnotations, savedAnnotations);
  }, [annotationDraftScope, attachment, currentAnnotations, open, savedAnnotations, setAnnotationDraft]);

  // Reset transform flag when attachment ID changes (new attachment)
  useEffect(() => {
    hasAppliedTransform.current = false;
    setCurrentGcsPath(undefined);
  }, [attachment?.attachmentId]);
  return <>
      {attachment && open && <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 overflow-hidden">
            <ImageAnnotator key={imageVersion} imageFile={imageFile} imageUrl={imageFile ? undefined : currentImageUrl} gcsPath={activeGcsPath} annotations={currentAnnotations} onAnnotationsChange={setCurrentAnnotations}
        // Rotate and crop replace a saved attachment's image; a template
        // image isn't one yet.
        onApplyTransform={attachment.attachmentId ? handleRequestTransform : undefined} isApplyingTransform={isApplyingTransform} className={cx(isFullScreen ? "h-full" : "h-[70vh] md:h-[85vh]", imageClassName)} attachmentId={attachment?.attachmentId} patientId={patientId} onViewHistory={handleViewHistory} extraActions={resolvedExtraActions} alwaysLabelMuscles={alwaysLabelMuscles} />
          </div>

          <AppointmentLocationPrompt locationId={appointment?.location?.id} appointmentId={appointment?.id} />

          <div className={cx("flex shrink-0 justify-end gap-2 border-t border-border bg-background py-1", isFullScreen ? "px-4" : "px-6", actionsClassName)}>
            {studio?.hasOriginal && <Button variant="secondary" onClick={() => setShowRestoreOriginal(true)} isLoading={revertToOriginal.isPending} disabled={isSaving || isApplyingTransform} className="mr-auto">
                Restore original
              </Button>}
            <Button variant="secondary" onClick={handleClose}>
              {closeLabel}
            </Button>
            <Button variant="primary" onClick={handleSave} isLoading={isSaving} disabled={isSaving || isApplyingTransform}>
              Save Annotations
            </Button>
          </div>
        </div>}

      {/* Transform Warning Modal */}
      <DeleteConfirmationModal isOpen={showTransformWarning} setIsOpen={setShowTransformWarning} onConfirm={handleConfirmTransform} title="Apply Transformation?" subtitle="Applying this transformation will clear all existing annotations. The original photo is kept, so you can restore it later." confirmText="Apply Transformation" />

      {/* Restore Original Modal (Studio Mode) */}
      <DeleteConfirmationModal isOpen={showRestoreOriginal} setIsOpen={setShowRestoreOriginal} onConfirm={() => {
      setShowRestoreOriginal(false);
      void handleRestoreOriginal();
    }} title="Restore Original Photo?" subtitle="This puts back the photo exactly as it was taken. Rotations, crops and annotations made since then are removed." confirmText="Restore Original" variant="primary" />

      {/* Annotation History Modal */}
      {attachment?.attachmentId && <AnnotationHistoryModal open={historyModalOpen} onOpenChange={setHistoryModalOpen} attachmentId={attachment.attachmentId} onRestoreComplete={handleRestoreComplete} onRestore={onRestore} isRestoring={isRestoring} currentAnnotations={currentAnnotations} />}
    </>;
}
interface AttachmentAnnotationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachment: NoteAttachment | null;
  /** Patient being charted, forwarded to the lot picker so it can offer lots
   *  today's checkout already drew for them. */
  patientId?: string;
  /** Appointment the photo was taken for. Face chart doses and the lots offered
   *  come from the location the user is working at, so the editor offers to
   *  switch to the appointment's location when it differs. */
  appointmentId?: string | null;
  initialAnnotations?: Annotation[];
  /**
   * Persist annotations to the server. Receives the latest local state PLUS
   * the baseline (what the server last saw) so the caller can compute a diff
   * and send a single bulk-apply request.
   */
  onSave: (annotations: Annotation[], baseline: Annotation[]) => Promise<void>;
  onImageReplaced?: () => void;
  isSaving?: boolean;
  imageFile?: File;
  onRestore?: (historyId: string) => Promise<void>;
  isRestoring?: boolean;
  extraActions?: AttachmentAnnotationExtraActions;
  annotationDraftScope?: AttachmentAnnotationDraftScope | null;
  alwaysLabelMuscles?: boolean;
}
export default function AttachmentAnnotationModal(t0) {
  const $ = _c(30);
  const {
    open,
    onOpenChange,
    attachment,
    patientId,
    appointmentId,
    initialAnnotations: t1,
    onSave,
    onImageReplaced,
    isSaving: t2,
    imageFile,
    onRestore,
    isRestoring: t3,
    extraActions: t4,
    annotationDraftScope: t5,
    alwaysLabelMuscles: t6
  } = t0;
  let t7;
  if ($[0] !== t1) {
    t7 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const initialAnnotations = t7;
  const isSaving = t2 === undefined ? false : t2;
  const isRestoring = t3 === undefined ? false : t3;
  const extraActions = t4 === undefined ? undefined : t4;
  const annotationDraftScope = t5 === undefined ? null : t5;
  const alwaysLabelMuscles = t6 === undefined ? false : t6;
  let t8;
  if ($[2] !== onOpenChange) {
    t8 = newOpen => {
      onOpenChange(newOpen);
    };
    $[2] = onOpenChange;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const handleOpenChange = t8;
  let t9;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <DialogHeader className="sr-only"><DialogTitle>Annotate Attachment</DialogTitle></DialogHeader>;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  let t10;
  if ($[5] !== onOpenChange) {
    t10 = () => onOpenChange(false);
    $[5] = onOpenChange;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  let t11;
  if ($[7] !== alwaysLabelMuscles || $[8] !== annotationDraftScope || $[9] !== appointmentId || $[10] !== attachment || $[11] !== extraActions || $[12] !== imageFile || $[13] !== initialAnnotations || $[14] !== isRestoring || $[15] !== isSaving || $[16] !== onImageReplaced || $[17] !== onRestore || $[18] !== onSave || $[19] !== open || $[20] !== patientId || $[21] !== t10) {
    t11 = <DialogBody className="min-h-0 flex-1 px-0 pb-0"><AttachmentAnnotationEditor open={open} attachment={attachment} patientId={patientId} appointmentId={appointmentId} initialAnnotations={initialAnnotations} onSave={onSave} onClose={t10} onImageReplaced={onImageReplaced} isSaving={isSaving} imageFile={imageFile} extraActions={extraActions} annotationDraftScope={annotationDraftScope} onRestore={onRestore} isRestoring={isRestoring} alwaysLabelMuscles={alwaysLabelMuscles} /></DialogBody>;
    $[7] = alwaysLabelMuscles;
    $[8] = annotationDraftScope;
    $[9] = appointmentId;
    $[10] = attachment;
    $[11] = extraActions;
    $[12] = imageFile;
    $[13] = initialAnnotations;
    $[14] = isRestoring;
    $[15] = isSaving;
    $[16] = onImageReplaced;
    $[17] = onRestore;
    $[18] = onSave;
    $[19] = open;
    $[20] = patientId;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <DialogFooter className="hidden" />;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t11) {
    t13 = <DialogContent className="flex max-h-[95vh] max-w-6xl flex-col px-0 md:max-w-[95vw] lg:max-w-6xl">{t9}{t11}{t12}</DialogContent>;
    $[24] = t11;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== handleOpenChange || $[27] !== open || $[28] !== t13) {
    t14 = <Dialog open={open} onOpenChange={handleOpenChange}>{t13}</Dialog>;
    $[26] = handleOpenChange;
    $[27] = open;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
}